/**
 * GAP-CP-032 — the canonical registry of `data-copilot-id` anchor ids the
 * copilot's `ui_highlight` client tool can target.
 *
 * `ui_highlight` resolves targets via
 * `document.querySelector('[data-copilot-id="<id>"]')`
 * (client-tool-registry.service.ts), so an id is only useful when a shipped
 * template actually stamps it. This module is the SINGLE source of truth both
 * sides share:
 *   - templates bind the ids from {@link COPILOT_ANCHOR_IDS} (each component's
 *     spec asserts its anchor renders into the DOM), and
 *   - the `ui_highlight` tool description advertises exactly
 *     {@link ALL_COPILOT_ANCHOR_IDS} to the model via
 *     {@link describeUiHighlightTargets} (asserted 1:1 in
 *     copilot-anchors.spec.ts).
 * Adding an id here without stamping it in a template will fail that
 * component-spec pairing — never ship an id the DOM cannot resolve.
 *
 * Ids are stable kebab-case, prefixed by the owning route surface (the routes
 * `ui_navigate` already knows), so the model can correlate "navigate to
 * /trading, then highlight trading-active-orders-table".
 */
export const COPILOT_ANCHOR_IDS = {
  /** /audit-logs — the audit events table (Investigate lives on its rows). */
  auditLogsTable: 'audit-logs-table',
  /** /dashboard — the alert notification widget (Investigate lives on its rows). */
  dashboardAlertsWidget: 'dashboard-alerts-widget',
  /** /trading — the active orders table. */
  activeOrdersTable: 'trading-active-orders-table',
  /** /trading — the open positions monitor table. */
  positionsTable: 'trading-positions-table',
  /** /strategies — the strategy management list table. */
  strategiesTable: 'strategies-list-table',
  /** /backtests — the backtest runs list table. */
  backtestsTable: 'backtests-list-table',
  /** /agent-proposals — the HITL proposal list. */
  agentProposalsList: 'agent-proposals-list',
} as const;

export type CopilotAnchorId =
  (typeof COPILOT_ANCHOR_IDS)[keyof typeof COPILOT_ANCHOR_IDS];

/** Every anchor id offered to the model, in stable declaration order. */
export const ALL_COPILOT_ANCHOR_IDS: readonly CopilotAnchorId[] =
  Object.values(COPILOT_ANCHOR_IDS);

/**
 * The `ui_highlight` tool-description fragment enumerating the valid targets.
 * Pure so copilot-anchors.spec.ts can assert every registered id is offered
 * to the model without instantiating the DI-heavy tool registry.
 */
export function describeUiHighlightTargets(): string {
  return `Valid ids: ${ALL_COPILOT_ANCHOR_IDS.join(', ')}.`;
}
